import { useEffect, useState } from "react";
import { DEFAULT_COL_WIDTH, HEADER_COL_WIDTH, EXTRA_SCROLL_COLS } from "../constants";

// Excel's hard column ceiling (column XFD).
const MAX_COLS = 16384;
// Start revealing more columns this far before the right edge so new
// columns are already there as the user scrolls into them.
const GROW_AHEAD_PX = 400;

/**
 * useColumnGrowth
 * Grows the number of rendered columns as the grid is scrolled to the
 * right, so the sheet behaves like Excel's "endless" column grid (A, B,
 * … Z, AA, AB, …) instead of stopping at the parsed column count.
 *
 * The target count is derived from the scroll position in PIXELS: enough
 * columns so the sheet's width always reaches `scrollLeft + viewport +
 * look-ahead`.  It is grow-only within a sheet (revealed columns stay) and
 * resets to the sheet's own column count when the sheet changes.
 *
 * Cheap: one rAF-throttled scroll/resize listener; no per-cell work here.
 */
export default function useColumnGrowth({ gridWrapperRef, baseCols, colWidths, activeSheet }) {
  const [displayCols, setDisplayCols] = useState(baseCols);

  // Reset to the sheet's own column count when the sheet (or its parsed
  // column count) changes.
  useEffect(() => { setDisplayCols(baseCols); }, [baseCols, activeSheet]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return undefined;

    // Total pixel width of the sheet's OWN columns (+ the row-header gutter).
    // Columns beyond `baseCols` are assumed to be the default width.
    let baseWidth = HEADER_COL_WIDTH;
    for (let i = 0; i < baseCols; i += 1) baseWidth += (colWidths[i] || DEFAULT_COL_WIDTH);

    let raf = 0;
    const recompute = () => {
      raf = 0;
      const need = el.scrollLeft + el.clientWidth + GROW_AHEAD_PX;
      let target = baseCols;
      if (need > baseWidth) {
        target = baseCols + Math.ceil((need - baseWidth) / DEFAULT_COL_WIDTH) + EXTRA_SCROLL_COLS;
      }
      target = Math.min(MAX_COLS, Math.max(baseCols, target));
      // Grow-only: never drop columns the user has already scrolled past.
      setDisplayCols((prev) => (target > prev ? target : prev));
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(recompute); };
    recompute(); // fill the viewport on first paint / sheet switch
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recompute) : null;
    if (ro) ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [gridWrapperRef, baseCols, colWidths, activeSheet]);

  return displayCols;
}
